import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useModal } from '@/contexts/modal'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getInvoiceMailTasks } from '@/modules/Invoice/services'
import type { InvoiceMailTask } from '@/modules/Invoice/types'
import type { ViewMetadataModalProps } from '@/modals/MailHistoryModals/ViewMetadata'
import { getMailHistoryColumns } from './columns'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'

interface MailHistoryProps {
    invoiceId: number
}

const MailHistory = ({ invoiceId }: MailHistoryProps) => {
    const { t } = useTranslation()
    const { setModal } = useModal()

    const [payload, setPayload] = useState<DataGridPayload>({
        page: 1,
        page_size: 10,
        q: '',
    })
    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INVOICE_MAIL_HISTORY, invoiceId],
        queryFn: () => getInvoiceMailTasks({ ...payload, invoiceId }),
        placeholderData: true,
    })

    const rows = data?.results || []

    const handleViewMetadata = (row: InvoiceMailTask) => {
        setModal<ViewMetadataModalProps>(
            'MailHistoryModals/ViewMetadata/index',
            {
                metadata: row.metadata,
                taskId: row.task_id,
            }
        )
    }

    const columns = useMemo(
        () => getMailHistoryColumns(t, handleViewMetadata),
        [t]
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="bg-white rounded-lg p-4 space-y-2">
            <Typogaphy className="text-primary font-medium">
                {t('mail-history')}
            </Typogaphy>
            <DataTableCustomized
                columns={columns}
                rows={rows}
                rowKey={(row) => row.task_id}
                pagination={{
                    page: payload.page,
                    pageSize: payload.page_size,
                    total: data?.pagination?.total || 0,
                    onPageChange: (page) => setPayload({ ...payload, page }),
                    onPageSizeChange: (pageSize) =>
                        setPayload({ ...payload, page_size: pageSize }),
                }}
            />
        </div>
    )
}

export default MailHistory
